import React, { useState, useEffect, useRef } from "react";
import useTheme from "../../../../common/hooks/useTheme";
import { Theme } from "../../../../providers/ThemeProvider";

interface PrescriptionStatusSelectProps {
  isActive: boolean;
  onStatusChange: (isActive: boolean) => void;
}

const PrescriptionStatusSelect: React.FC<PrescriptionStatusSelectProps> = ({
  isActive,
  onStatusChange,
}) => {
  const theme: Theme = useTheme();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Convert to boolean if needed (handles 0/1 from API)
  const isActiveBoolean = Boolean(isActive);

  const handleToggle = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setOpen(!open);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        open &&
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        handleClose();
      }
    };

    if (open) {
      const timeoutId = setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 10);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [open]);

  const handleOptionClick = (newIsActive: boolean) => {
    if (newIsActive !== isActiveBoolean) {
      onStatusChange(newIsActive);
    }
    handleClose();
  };

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      {/* Status button */}
      <div
        style={styles.statusButton(theme, isActiveBoolean)}
        onClick={handleToggle}
      >
        <span style={styles.statusText(theme)}>
          {isActiveBoolean ? "Active" : "Inactive"}
        </span>
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          style={{
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 200ms",
          }}
        >
          <path
            d="M7.99943 11.0006C7.73424 11.0005 7.47992 10.8951 7.29243 10.7076L3.29243 6.70757C3.11027 6.51897 3.00948 6.26636 3.01176 6.00417C3.01403 5.74197 3.1192 5.49116 3.30461 5.30575C3.49002 5.12034 3.74083 5.01517 4.00303 5.0129C4.26523 5.01062 4.51783 5.11141 4.70643 5.29357L7.99943 8.58657L11.2924 5.29357C11.3847 5.19806 11.495 5.12188 11.617 5.06947C11.739 5.01706 11.8703 4.98947 12.003 4.98832C12.1358 4.98717 12.2675 5.01247 12.3904 5.06275C12.5133 5.11303 12.6249 5.18728 12.7188 5.28117C12.8127 5.37507 12.887 5.48672 12.9373 5.60962C12.9875 5.73251 13.0128 5.86419 13.0117 5.99697C13.0105 6.12975 12.9829 6.26097 12.9305 6.38297C12.8781 6.50498 12.8019 6.61532 12.7064 6.70757L8.70643 10.7076C8.51894 10.8951 8.26463 11.0005 7.99943 11.0006Z"
            fill={theme.colors_ds.NEUTRAL_TEXT_DEFAULT}
          />
        </svg>
      </div>

      {/* Dropdown menu */}
      {open && (
        <div style={styles.dropdown(theme)}>
          <div
            onClick={() => handleOptionClick(true)}
            style={{
              ...styles.option(theme),
              backgroundColor: isActiveBoolean
                ? theme.colorTokens.N[40]
                : "transparent",
            }}
          >
            <div style={styles.dot(theme, true)} />
            Active
          </div>
          <div
            onClick={() => handleOptionClick(false)}
            style={{
              ...styles.option(theme),
              backgroundColor: !isActiveBoolean
                ? theme.colorTokens.N[40]
                : "transparent",
            }}
          >
            <div style={styles.dot(theme, false)} />
            Inactive
          </div>
        </div>
      )}
    </div>
  );
};

export default PrescriptionStatusSelect;

const styles = {
  statusButton: (theme: Theme, isActive: boolean) => ({
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "4px 12px",
    backgroundColor: isActive
      ? theme.colorTokens.S[10]
      : theme.colorTokens.N[20],
    border: isActive
      ? `1px solid ${theme.colorTokens.S[70]}`
      : `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s",
    ":hover": {
      opacity: 0.8,
    },
  }),
  statusText: (theme: Theme) => ({
    fontFamily: "var(--font-Medium)",
    fontSize: "14px",
    lineHeight: "1.6",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  dropdown: (theme: Theme) => ({
    position: "absolute" as "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    minWidth: "120px",
    backgroundColor: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
    borderRadius: "8px",
    boxShadow: "var(--box-shadow-general)",
    zIndex: 1000,
    overflow: "hidden",
    padding: "4px",
  }),
  option: (theme: Theme) => ({
    width: "100%",
    padding: "4px 8px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    backgroundColor: "transparent",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
    fontFamily: "var(--font-Medium)",
    fontSize: "12px",
    cursor: "pointer",
    borderRadius: "8px",
  }),
  dot: (theme: Theme, isActive: boolean) => ({
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    backgroundColor: isActive
      ? theme.colorTokens.S[70]
      : theme.colorTokens.N[80],
  }),
};
